import { Select } from "@vellum/ui";
import { vellumApiSdk } from "@vellum/micro-app-auth";
import type { AdminUserOption } from "@vellum/vellum-api";
import { useEffect, useRef, useState } from "react";

interface FlagUserSelectProps {
  /** Currently targeted Luna user ids. */
  value: string[];
  onChange: (userIds: string[]) => void;
}

const optionLabel = (user: AdminUserOption) => `${user.name} (${user.email})`;

/**
 * Per-user targeting picker backed by the shared admin user lookup. Typing
 * queries the API; selections persist as user ids. Existing ids are resolved
 * to names on open so an edited flag shows who it already targets.
 */
export function FlagUserSelect({ value, onChange }: FlagUserSelectProps) {
  const [selected, setSelected] = useState<AdminUserOption[]>([]);
  const [options, setOptions] = useState<AdminUserOption[]>([]);
  const [loading, setLoading] = useState(false);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Resolve the incoming user ids to full options (name/email) once, so the
  // control can render existing selections instead of raw ids.
  useEffect(() => {
    const unresolved = value.filter(
      (id) => !selected.some((option) => option.id === id),
    );
    if (unresolved.length === 0) {
      return;
    }

    let active = true;
    void (async () => {
      const result = await vellumApiSdk.adminSearchUserOptions({
        query: { ids: unresolved },
      });
      if (active && result.data) {
        setSelected((current) => {
          const known = new Set(current.map((option) => option.id));
          const additions = result.data.filter(
            (option) => !known.has(option.id),
          );
          return [...current, ...additions];
        });
      }
    })();

    return () => {
      active = false;
    };
    // Intentionally keyed on the ids only; `selected` is read fresh above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.join(",")]);

  const runSearch = (query: string) => {
    setLoading(true);
    void (async () => {
      const result = await vellumApiSdk.adminSearchUserOptions({
        query: { q: query || null },
      });
      setOptions(result.data ?? []);
      setLoading(false);
    })();
  };

  const handleInputChange = (input: string) => {
    if (debounce.current) {
      clearTimeout(debounce.current);
    }
    debounce.current = setTimeout(() => runSearch(input), 250);
  };

  const handleChange = (next: readonly AdminUserOption[] | null) => {
    const nextSelected = [...(next ?? [])];
    setSelected(nextSelected);
    onChange(nextSelected.map((option) => option.id));
  };

  return (
    <div>
      <Select
        name="user_ids"
        label="Specific users"
        isMulti
        isClearable
        // Server-side search already narrows the list; don't filter again.
        filterOption={() => true}
        options={options}
        value={selected}
        isLoading={loading}
        getOptionValue={(option: AdminUserOption) => option.id}
        getOptionLabel={optionLabel}
        onInputChange={handleInputChange}
        onChange={handleChange}
        onMenuOpen={() => runSearch("")}
        placeholder="Search users by name or email…"
        noOptionsMessage={() => "No matching users"}
      />
      <p className="mt-1.5 text-xs text-gray-500">
        {selected.length === 0
          ? "No individual users targeted."
          : `${selected.length} user${selected.length === 1 ? "" : "s"} targeted.`}
      </p>
    </div>
  );
}
